// Twilio SMS/MMS notification pricing
//
// Pricing tiers by recipient country:
// - US & Canada: 30 messages per credit (1/30 = 0.0333... credits per message)
// - Other supported countries: 10 messages per credit (1/10 = 0.1 credits per message)
//
// SMS and MMS are priced the same.

// Country calling codes for pricing tiers
// US (+1) includes US, Canada, and other NANP countries
const US_CANADA_CALLING_CODES = ["+1"];

// Blocked countries (expensive rates that would cause losses)
// These are blocked and will return an error
// Note: +7 is shared by Russia and Kazakhstan. Kazakhstan uses +76 and +77 prefixes,
// which are explicitly allowed below.
export const BLOCKED_COUNTRY_CODES: { [key: string]: string } = {
    "+387": "Bosnia and Herzegovina",
    "+261": "Madagascar",
    "+675": "Papua New Guinea",
    "+7": "Russia",
    "+216": "Tunisia"
};

// Kazakhstan prefixes - these are allowed even though they start with +7
// Kazakhstan uses +76 and +77, while Russia uses other +7 prefixes
const KAZAKHSTAN_PREFIXES = ["+76", "+77"];

// Credits per message for US/Canada recipients
// 30 messages per credit = 1/30 credits per message
const CREDITS_PER_MESSAGE_US_CANADA = 1 / 30;

// Credits per message for other supported countries
// 10 messages per credit = 1/10 credits per message
const CREDITS_PER_MESSAGE_OTHER = 1 / 10;

/**
 * Determines the country from a phone number in E.164 format.
 * Returns an object with the country code prefix and whether it's a blocked country.
 */
export function getCountryFromPhoneNumber(phoneNumber: string): {
    countryCode: string;
    countryName: string | null;
    isBlocked: boolean;
    isUsOrCanada: boolean;
} {
    // Check for Kazakhstan first (+76, +77) - these are allowed even though +7 is blocked
    for (const prefix of KAZAKHSTAN_PREFIXES) {
        if (phoneNumber.startsWith(prefix)) {
            return {
                countryCode: prefix,
                countryName: "Kazakhstan",
                isBlocked: false,
                isUsOrCanada: false
            };
        }
    }

    // Check for blocked countries (longer codes first to avoid false matches)
    for (const [code, name] of Object.entries(BLOCKED_COUNTRY_CODES)) {
        if (phoneNumber.startsWith(code)) {
            return { countryCode: code, countryName: name, isBlocked: true, isUsOrCanada: false };
        }
    }

    // Check for US/Canada (NANP +1)
    for (const code of US_CANADA_CALLING_CODES) {
        if (phoneNumber.startsWith(code)) {
            return {
                countryCode: code,
                countryName: "US/Canada",
                isBlocked: false,
                isUsOrCanada: true
            };
        }
    }

    // Other countries - extract country code (simplified - first 1-4 digits after +)
    // This is a simplified extraction; exact country code matching would require a full database
    const match = phoneNumber.match(/^\+(\d{1,4})/);
    return {
        countryCode: match ? `+${match[1]}` : "unknown",
        countryName: null,
        isBlocked: false,
        isUsOrCanada: false
    };
}

/**
 * Get the credit conversion rate for a message to a specific phone number.
 *
 * @param phoneNumber - Recipient phone number in E.164 format
 * @returns Credits per message for this destination
 * @throws Error if the destination country is blocked
 */
export function getConversionRateForDestination(phoneNumber: string): number {
    const countryInfo = getCountryFromPhoneNumber(phoneNumber);

    if (countryInfo.isBlocked) {
        throw new Error(
            `Sending messages to ${countryInfo.countryName} (${countryInfo.countryCode}) is not supported. ` +
                `Please use your own Twilio account for this destination.`
        );
    }

    return countryInfo.isUsOrCanada ? CREDITS_PER_MESSAGE_US_CANADA : CREDITS_PER_MESSAGE_OTHER;
}

/**
 * Get the credit conversion rate for SMS notifications (US/Canada rate as default).
 * This is used for default billing configuration display.
 */
export function getSmsConversionRate(): number {
    return CREDITS_PER_MESSAGE_US_CANADA;
}

/**
 * Get the credit conversion rate for MMS notifications (US/Canada rate as default).
 * This is used for default billing configuration display.
 */
export function getMmsConversionRate(): number {
    return CREDITS_PER_MESSAGE_US_CANADA;
}

/**
 * Get the conversion rate for international SMS (non-US/Canada).
 */
export function getSmsInternationalConversionRate(): number {
    return CREDITS_PER_MESSAGE_OTHER;
}

/**
 * Get the conversion rate for international MMS (non-US/Canada).
 */
export function getMmsInternationalConversionRate(): number {
    return CREDITS_PER_MESSAGE_OTHER;
}
